import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { GripVertical, Eye, EyeOff, RotateCcw, Save, Check } from 'lucide-react';
import { cn } from '../design-system';
import {
  getSidebarPreferences,
  saveSidebarPreferences,
  resetSidebarPreferences,
  DEFAULT_SECTION_ORDER,
} from '../../utils/sidebarPreferences';

// Navigation sections definition (must match Sidebar.js)
const NAV_SECTIONS = [
  {
    id: 'playerDevelopment',
    label: 'Player Development',
    items: [
      { id: 'videoAnalytics', label: 'Video Analytics' },
      { id: 'videoAnalytics', label: 'Video Analytics' },
    ],
  },
  {
    id: 'rosterManagement',
    label: 'Roster Management',
    items: [{ id: 'myTeam', label: 'Roster Management' }],
  },
  {
    id: 'scouting',
    label: 'Scouting',
    items: [
      { id: 'askSlamAI', label: 'Ask SLAM AI' },
      { id: 'selfScouting', label: 'Self Scouting' },
      { id: 'opponentScouting', label: 'Opponent Scouting' },
    ],
  },
  {
    id: 'playerAcquisition',
    label: 'Player Acquisition',
    items: [
      { id: 'transferPortal', label: 'Transfer Portal' },
      { id: 'freeAgentDashboard', label: 'Free Agent Dashboard' },
      { id: 'tradeTargets', label: 'Trade Targets' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { id: 'settings', label: 'Settings' },
      { id: 'theWire', label: 'The Wire' },
      { id: 'reports', label: 'Reports' },
      { id: 'integrations', label: 'Integrations' },
      { id: 'canvas', label: 'Agent Builder' },
    ],
  },
];

const NavigationSettings = ({ isDarkMode = true }) => {
  const [preferences, setPreferences] = useState(() => getSidebarPreferences());
  const [hasChanges, setHasChanges] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  // Sort sections by preference order
  const orderedSections = [...NAV_SECTIONS].sort((a, b) => {
    const orderA = preferences.sectionOrder.indexOf(a.id);
    const orderB = preferences.sectionOrder.indexOf(b.id);
    const posA = orderA === -1 ? 999 : orderA;
    const posB = orderB === -1 ? 999 : orderB;
    return posA - posB;
  });

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(orderedSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const newOrder = items.map((s) => s.id);
    setPreferences((prev) => ({ ...prev, sectionOrder: newOrder }));
    setHasChanges(true);
  };

  const toggleItemVisibility = (itemId) => {
    setPreferences((prev) => {
      const isHidden = prev.hiddenItems.includes(itemId);
      return {
        ...prev,
        hiddenItems: isHidden
          ? prev.hiddenItems.filter((id) => id !== itemId)
          : [...prev.hiddenItems, itemId],
      };
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    saveSidebarPreferences(preferences);
    setHasChanges(false);
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const handleReset = () => {
    resetSidebarPreferences();
    setPreferences({
      version: 1,
      hiddenItems: [],
      sectionOrder: DEFAULT_SECTION_ORDER,
      updatedAt: null,
    });
    setHasChanges(true);
  };

  const isItemHidden = (itemId) => preferences.hiddenItems.includes(itemId);

  // Styles — OLED Black & Gold
  const sectionClass = cn(
    'rounded-[14px] border mb-2 overflow-hidden',
    'border-[#1A1A1A] bg-[#111111]'
  );

  const itemClass = (isHidden) =>
    cn(
      'flex items-center justify-between px-4 py-2 border-b last:border-b-0 transition-colors',
      'border-[#1A1A1A]',
      isHidden && 'opacity-50'
    );

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm text-[#F5F5F5]">Sidebar Navigation</h3>
          <p className="text-xs text-[#555555]">
            Drag sections to reorder. Toggle items to show/hide.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showSaved && (
            <span className="text-xs text-green-500 flex items-center gap-1">
              <Check size={14} /> Saved
            </span>
          )}
          <button
            onClick={handleReset}
            className="p-2 rounded-lg text-xs flex items-center gap-1 transition-colors hover:bg-[rgba(255,255,255,0.04)] text-[#999999]"
            title="Reset to defaults"
          >
            <RotateCcw size={14} />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors',
              hasChanges
                ? 'bg-[#E8B800] text-black hover:bg-[#d4a800]'
                : 'bg-[#E8B800]/40 text-black/50 opacity-50 cursor-not-allowed'
            )}
          >
            <Save size={14} />
            Save
          </button>
        </div>
      </div>

      {/* Drag and drop sections */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {orderedSections.map((section, index) => (
                <Draggable key={section.id} draggableId={section.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        sectionClass,
                        snapshot.isDragging && 'shadow-lg ring-2 ring-[rgba(232,184,0,0.5)]'
                      )}
                    >
                      {/* Section header (draggable) */}
                      <div
                        {...provided.dragHandleProps}
                        className="flex items-center gap-2 px-3 py-2 cursor-grab active:cursor-grabbing bg-[#0A0A0A] border-b border-[#1A1A1A]"
                      >
                        <GripVertical
                          size={16}
                          className="text-[#555555] flex-shrink-0"
                        />
                        <span className="font-medium text-sm text-[#F5F5F5]">{section.label}</span>
                      </div>

                      {/* Section items */}
                      <div>
                        {section.items.map((item) => {
                          const hidden = isItemHidden(item.id);
                          return (
                            <div key={item.id} className={itemClass(hidden)}>
                              <span className="text-sm text-[#F5F5F5]">{item.label}</span>
                              <button
                                onClick={() => toggleItemVisibility(item.id)}
                                className={cn(
                                  'p-1.5 rounded transition-colors',
                                  hidden
                                    ? 'text-[#555555] hover:text-[#F5F5F5]'
                                    : 'text-[#E8B800] hover:text-[#d4a800]'
                                )}
                                title={hidden ? 'Show item' : 'Hide item'}
                              >
                                {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Info text */}
      <p className="text-xs text-[#555555]">
        Changes are applied to your sidebar immediately after saving. Hidden items can be
        restored at any time.
      </p>
    </div>
  );
};

export default NavigationSettings;
